import { axiosInstance } from './api';

// Note: All person IDs should be UUIDs as per the Django API specification

// Core Person Data Endpoint
export const getPerson = async (personId) => {
  try {
    const response = await axiosInstance.get(`/genealogy/people/${personId}/`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Family Tree Structure Endpoint
export const getFamilyTree = async (rootPersonId, generations = 5) => {
  try {
    const response = await axiosInstance.get(
      `/genealogy/people/${rootPersonId}/family_tree/`
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Enhanced Balkan Tree Endpoint with generation mapping - matches specified pattern
export const fetchFamilyTreeData = async () => {
  try {
    const response = await axiosInstance.get('/genealogy/people/balkan_tree/', {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching family tree data:', error);
    throw error;
  }
};

// Legacy Balkan Tree Endpoint - Keep for backward compatibility
export const getBalkanTree = async () => {
  try {
    const response = await axiosInstance.get('/genealogy/people/balkan_tree/');
    return response.data;
  } catch (error) {
    console.error('Error fetching balkan tree:', error);
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Relationship Data Endpoint
export const getPersonRelationships = async (personId) => {
  try {
    const response = await axiosInstance.get(`/genealogy/relationships/?person_id=${personId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Get all persons for the current user's family
export const getAllFamilyPersons = async () => {
  try {
    const response = await axiosInstance.get('/genealogy/people/');
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Search persons by name
export const searchPersons = async (searchTerm) => {
  try {
    const response = await axiosInstance.get(`/genealogy/family-tree/search_people/?q=${encodeURIComponent(searchTerm)}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Create a new person
export const createPerson = async (personData) => {
  try {
    const response = await axiosInstance.post('/genealogy/people/', personData);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Update a person
export const updatePerson = async (personId, personData) => {
  try {
    const response = await axiosInstance.put(`/genealogy/people/${personId}/`, personData);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};

// Fetch a tree segment centered on a person, traversing relationships
export const fetchTreeSegment = async (personId, direction = 'all', depth = 3, excludeIds = '') => {
  const params = new URLSearchParams({ person_id: personId, direction, depth: String(depth) });
  if (excludeIds) params.set('exclude_ids', excludeIds);
  const response = await axiosInstance.get(`/genealogy/people/tree_segment/?${params}`);
  return response.data;
};

// Batch check family membership for multiple persons
export const fetchFamilyMembershipBatch = async (personIds, familyId) => {
  const response = await axiosInstance.get(
    `/genealogy/people/family_membership_batch/?person_ids=${personIds.join(',')}&family_id=${familyId}`
  );
  return response.data;
};

// Add an immediate family member to the tree (creates Person + Relationship)
export const addTreeRelative = async (data) => {
  const response = await axiosInstance.post('/genealogy/people/add_tree_relative/', data);
  return response.data;
};

// Create a relationship
export const createRelationship = async (relationshipData) => {
  try {
    const response = await axiosInstance.post('/genealogy/relationships/', relationshipData);
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error("Error response:", error.response.data);
      throw new Error(error.response.data.message || JSON.stringify(error.response.data));
    } else if (error.request) {
      console.error("Error request:", error.request);
      throw new Error('No response received from server');
    } else {
      console.error("Error message:", error.message);
      throw new Error('An unexpected error occurred');
    }
  }
};
